import { userManager } from '@teko/auth';
import { useAuth } from '@teko/auth-react';
import { useEffect, useMemo } from 'react';

const CACHE_KEY = 'cached_profile_id_by_sub';
const TEKOID_PREFIX = 'tekoid.user.';

// On the first render after F5 window.TekoID isn't attached yet, so fall back to
// the session the SDK persists at `tekoid.user.<clientId>`.
const getUserSub = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  try {
    const sub = userManager.tekoID?.user?.getUserInfo()?.sub;
    if (sub) return sub;
    const key = Object.keys(localStorage).find((k) =>
      k.startsWith(TEKOID_PREFIX),
    );
    const raw = key && localStorage.getItem(key);
    return raw ? JSON.parse(raw)?.profile?.sub : undefined;
  } catch {
    return undefined;
  }
};

/**
 * profileId (user.id) only resolves after myProfile, so the first search fetch after
 * F5 goes out without userId and refetches once it arrives — a visible flash. Cache it
 * as `<sub>:<profileId>` so the next F5 seeds it on the first render: one request. The
 * live session's sub gates the read, so a stale entry is never used.
 */
export const useCachedProfileId = (): string | undefined => {
  const { user } = useAuth();
  const id = user?.id;
  const sub = useMemo(getUserSub, [id]);

  useEffect(() => {
    if (!sub || !id) return;
    try {
      localStorage.setItem(CACHE_KEY, `${sub}:${id}`);
    } catch {
      // best-effort
    }
  }, [id, sub]);

  if (id) return id;
  if (!sub) return undefined;
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw?.startsWith(`${sub}:`) ? raw.slice(sub.length + 1) : undefined;
  } catch {
    return undefined;
  }
};
